import { Injectable, signal, type Signal } from '@angular/core';

export type ThemeMode = 'light' | 'dark';

const THEME_STORAGE_KEY: string = 'nexus_theme_mode';
const THEME_META_COLOR_LIGHT: string = '#f8f8ff';
const THEME_META_COLOR_DARK: string = '#10131d';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly modeSignal = signal<ThemeMode>('light');
  public readonly mode: Signal<ThemeMode> = this.modeSignal.asReadonly();

  public initialize(): void {
    const initialMode: ThemeMode = this.readStoredMode() ?? this.detectSystemMode();
    this.applyMode(initialMode);
  }

  public toggleTheme(): void {
    const nextMode: ThemeMode = this.modeSignal() === 'dark' ? 'light' : 'dark';
    this.applyMode(nextMode);
  }

  private readStoredMode(): ThemeMode | undefined {
    if (typeof localStorage === 'undefined') {
      return undefined;
    }

    const rawValue: string | null = localStorage.getItem(THEME_STORAGE_KEY);
    if (rawValue === 'light' || rawValue === 'dark') {
      return rawValue;
    }
    return undefined;
  }

  private detectSystemMode(): ThemeMode {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return 'light';
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private applyMode(mode: ThemeMode): void {
    this.modeSignal.set(mode);
    if (typeof document !== 'undefined') {
      document.documentElement.dataset['theme'] = mode;
      this.applyThemeColorMeta(mode);
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    }
  }

  private applyThemeColorMeta(mode: ThemeMode): void {
    if (typeof document === 'undefined') {
      return;
    }

    const metaElement: HTMLMetaElement | null = document.querySelector<HTMLMetaElement>(
      'meta[name="theme-color"]',
    );
    if (!metaElement) {
      return;
    }

    const themeColor: string = mode === 'dark' ? THEME_META_COLOR_DARK : THEME_META_COLOR_LIGHT;
    metaElement.setAttribute('content', themeColor);
  }
}
